import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/utils";
import {
  ChevronLeft,
  Sparkles,
  Send,
  Loader2,
  Check,
  X,
  Copy,
  Edit2,
  Plus,
  ArrowRight,
  Code,
  CheckCircle2,
  AlertCircle,
  Wand2,
  ExternalLink,
  Zap,
  Settings2,
  MessageSquare,
  Globe,
  RefreshCw,
  Target,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import MessagingStep from "@/components/messaging/MessagingStep";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Goal category display names mapping
const GOAL_CATEGORY_NAMES = {
  resonance_message_match: "Resonance & Message Match",
  product_clarity: "Product Clarity",
  eligibility_fit: "Eligibility / Fit",
  objection_risk_removal: "Objection Removal",
  comparison_differentiation: "Comparison",
  conversion_path: "Conversion Path",
  post_conversation: "Post-Conversation",
  stakeholder_enablement: "Stakeholder Enablement",
  urgency_why_now: "Urgency",
  activation_readiness: "Activation Readiness",
  // Legacy categories (for backward compatibility)
  persona_resonance: "Persona Resonance",
  product_explanation: "Product Explanation",
  objection_handling: "Objection Handling",
  competitive_positioning: "Competitive Positioning",
  credibility: "Credibility",
  conversation_continuity: "Conversation Continuity",
  confidence_building: "Confidence Building",
  cta_optimization: "CTA Optimization"
};

const GOAL_CATEGORY_COLORS = {
  resonance_message_match: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  product_clarity: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  eligibility_fit: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  objection_risk_removal: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  comparison_differentiation: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  conversion_path: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
  post_conversation: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  stakeholder_enablement: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
  urgency_why_now: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  activation_readiness: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"
};

// GoalCard component to display individual goals
function GoalCard({ goal, onToggle }) {
  const actions = goal.recommended_actions || [];
  const evidence = goal.evidence || [];
  const categoryName = GOAL_CATEGORY_NAMES[goal.goal_category] || goal.goal_category;
  const categoryColor = GOAL_CATEGORY_COLORS[goal.goal_category] || "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
  
  // Confidence indicator color
  const getConfidenceColor = (conf) => {
    if (conf >= 0.85) return "text-green-600 dark:text-green-400";
    if (conf >= 0.65) return "text-amber-600 dark:text-amber-400";
    return "text-red-600 dark:text-red-400";
  };
  
  return (
    <div
      className={`rounded-xl border-2 overflow-hidden transition-all ${goal.is_approved ? 'ring-2 ring-primary/30 border-primary/50 bg-primary/5' : 'border-border'}`}
      data-testid={`goal-${goal.id}`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={goal.is_approved}
            onChange={() => onToggle(goal)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            data-testid={`goal-checkbox-${goal.id}`}
          />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${categoryColor}`}>
                {categoryName}
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
                #{goal.priority}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded bg-muted ${getConfidenceColor(goal.confidence || 0.5)}`}>
                {Math.round((goal.confidence || 0.5) * 100)}% confidence
              </span>
            </div>
            <p className="text-sm text-foreground font-medium">{goal.goal_text}</p>
            
            {goal.reasoning && (
              <div className="mt-2 p-2 bg-muted/50 rounded-lg">
                <p className="text-xs font-semibold text-muted-foreground mb-1">WHY THIS GOAL:</p>
                <p className="text-xs text-foreground">{goal.reasoning}</p>
              </div>
            )}
            
            {evidence.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-semibold text-muted-foreground mb-1">EVIDENCE:</p>
                <ul className="space-y-1">
                  {evidence.map((item, idx) => (
                    <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                      <span className="text-green-500">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {actions.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-muted-foreground mb-1">RECOMMENDED ACTIONS:</p>
                <ul className="space-y-1">
                  {actions.map((action, idx) => (
                    <li key={idx} className="text-xs text-foreground flex items-start gap-2">
                      <span className="text-primary">→</span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// OutcomeGoalCard component
function OutcomeGoalCard({ outcomeGoal }) {
  const evidenceList = outcomeGoal.evidence || [];
  return (
    <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-4 border border-green-500/30">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-500" /> Primary Outcome
        </h3>
        <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
          {Math.round((outcomeGoal.confidence || 0.5) * 100)}% confidence
        </span>
      </div>
      <p className="text-lg font-medium text-foreground capitalize">{outcomeGoal.outcome}</p>
      {evidenceList.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-muted-foreground">Evidence:</p>
          {evidenceList.map((e, i) => (
            <p key={i} className="text-xs text-muted-foreground ml-2">• {e}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// RelationshipExplainerCard component
function RelationshipExplainerCard({ items }) {
  return (
    <div className="bg-muted/20 rounded-xl p-4 border border-border">
      <h4 className="text-xs font-semibold text-muted-foreground mb-2">HOW STRATEGIES DRIVE THE OUTCOME:</h4>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-sm text-foreground flex items-start gap-2">
            <span className="text-primary">→</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ExcludedGoalsCard component
function ExcludedGoalsCard({ items }) {
  return (
    <div className="bg-muted/10 rounded-xl p-4 border border-border/50">
      <h4 className="text-xs font-semibold text-muted-foreground mb-2">EXCLUDED STRATEGIES (not needed for this campaign):</h4>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{item.goal_name}:</span> {item.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Fallback old messaging change card (for backward compatibility)
function OldMessagingChangeCard({ 
  change, 
  onToggle, 
  onEdit, 
  onSave, 
  onCancel, 
  editingId, 
  editingText, 
  setEditingText, 
  saving 
}) {
  const isEditing = editingId === change.id;
  
  return (
    <div className={`rounded-xl border-2 overflow-hidden ${change.is_approved ? 'ring-2 ring-green-500/30 border-green-500/50' : 'border-border'}`}>
      <div className="p-4 border-b border-border bg-surface/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
              {change.section_type}
            </span>
          </div>
          <Button
            variant={change.is_approved ? "default" : "outline"}
            size="sm"
            onClick={() => onToggle(change)}
            className={`rounded-full ${change.is_approved ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
          >
            {change.is_approved ? (
              <><CheckCircle2 className="w-4 h-4 mr-1" /> Approved</>
            ) : (
              <><Check className="w-4 h-4 mr-1" /> Approve</>
            )}
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="p-4 border-b md:border-b-0 md:border-r border-border">
          <p className="text-xs font-semibold text-red-600 mb-2 flex items-center gap-1">
            <X className="w-3 h-3" /> Original
          </p>
          <p className="text-sm text-foreground">{change.original_content}</p>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-green-600 flex items-center gap-1">
              <Check className="w-3 h-3" /> Proposed
            </p>
            {!isEditing && (
              <button onClick={() => onEdit(change)} className="text-xs text-muted-foreground hover:text-primary">
                <Edit2 className="w-3 h-3" />
              </button>
            )}
          </div>
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                className="w-full p-2 border rounded text-sm min-h-[80px]"
              />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
                <Button size="sm" onClick={() => onSave(change)} disabled={saving}>Save</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-foreground">{change.proposed_content}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CampaignWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("context"); // context, goals, messaging, activation
  const [mode, setMode] = useState("manual"); // "manual" or "auto"
  const [autoProcessing, setAutoProcessing] = useState(false);
  const [strategySummary, setStrategySummary] = useState("");
  
  // Context state - single unified context
  const [campaignContext, setCampaignContext] = useState("");
  const [contextTab, setContextTab] = useState("write"); // "write" or "chat"
  const [chatInput, setChatInput] = useState("");
  const [extractedContext, setExtractedContext] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Website context state (extracted from scraped site)
  const [websiteContext, setWebsiteContext] = useState(null);
  const [loadingWebsiteContext, setLoadingWebsiteContext] = useState(false);
  
  // Scraped sections for editing in messaging step
  const [scrapedSections, setScrapedSections] = useState([]);
  
  // Goals state (replaces concerns)
  const [goals, setGoals] = useState([]);
  const [outcomeGoal, setOutcomeGoal] = useState(null);
  const [visitorAnalysis, setVisitorAnalysis] = useState(null);
  const [relationshipExplainer, setRelationshipExplainer] = useState([]);
  const [excludedGoals, setExcludedGoals] = useState([]);
  const [generatingMessaging, setGeneratingMessaging] = useState(false);
  
  // Section Strategy state (new)
  const [sectionStrategy, setSectionStrategy] = useState(null);
  const [generatingStrategy, setGeneratingStrategy] = useState(false);
  
  // Messaging state
  const [messagingChanges, setMessagingChanges] = useState([]);
  const [editingChangeId, setEditingChangeId] = useState(null);
  const [editingChangeText, setEditingChangeText] = useState("");
  const [savingChange, setSavingChange] = useState(false);
  const [activating, setActivating] = useState(false);
  const [messagingMode, setMessagingMode] = useState('impact'); // 'subtle' or 'impact'
  
  // Activation state
  const [config, setConfig] = useState(null);
  const [copied, setCopied] = useState(false);
  const [magicLink, setMagicLink] = useState(null);
  const [creatingMagicLink, setCreatingMagicLink] = useState(false);

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  useEffect(() => {
    if (campaign) {
      // Load mode from campaign
      setMode(campaign.mode || "manual");
      
      // Fetch website context if campaign is scraped
      if (campaign.status !== "scraping") {
        fetchWebsiteContext();
      }
      
      // Load context from campaign if available (combine into single field)
      if (campaign.campaign_context) {
        setCampaignContext(campaign.campaign_context);
      } else if (campaign.visitor_background || campaign.visitor_intent || campaign.campaign_goal) {
        // Legacy: combine old fields into single context
        const parts = [];
        if (campaign.visitor_background) parts.push(`Visitor: ${campaign.visitor_background}`);
        if (campaign.visitor_intent) parts.push(`Intent: ${campaign.visitor_intent}`);
        if (campaign.campaign_goal) parts.push(`Goal: ${campaign.campaign_goal}`);
        setCampaignContext(parts.join('\n\n'));
      }
      
      // Determine step based on campaign status
      const status = campaign.status;
      if (status === "active") {
        setStep("activation");
        fetchConfig();
        fetchGoals();
        fetchMessaging();
        fetchSectionStrategy();
        fetchScrapedSections();
      } else if (status === "messaging_confirmed") {
        // Messaging confirmed - go to activation
        setStep("activation");
        fetchConfig();
        fetchMessaging();
        fetchSectionStrategy();
        fetchScrapedSections();
        fetchGoals();
      } else if (status === "messaging_ready") {
        setStep("messaging");
        fetchMessaging();
        fetchSectionStrategy();
        fetchScrapedSections();
        fetchGoals();
      } else if (status === "goals_ready" || status === "goals_confirmed") {
        setStep("goals");
        fetchGoals();
      } else {
        setStep("context");
      }
    }
  }, [campaign]);

  // Fetch data when step changes (for navigation back)
  useEffect(() => {
    if (step === "goals") {
      fetchGoals();
    } else if (step === "messaging") {
      fetchMessaging();
      fetchSectionStrategy();
      fetchScrapedSections();
      fetchGoals();
    } else if (step === "activation") {
      fetchConfig();
    }
  }, [step]);

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${id}`);
      setCampaign(response.data);
    } catch (error) {
      toast.error("Failed to load campaign");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${id}/goals`);
      setGoals(response.data.goals || []);
    } catch (error) {
      console.error("Failed to fetch goals");
    }
  };

  const fetchMessaging = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${id}/messaging`);
      setMessagingChanges(response.data);
    } catch (error) {
      console.error("Failed to fetch messaging");
    }
  };
  
  const fetchSectionStrategy = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${id}/section-strategy`);
      if (response.data && !response.data.error) {
        setSectionStrategy(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch section strategy");
    }
  };
  
  const fetchScrapedSections = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${id}/scraped-content`);
      if (response.data) {
        console.log("Scraped sections loaded:", response.data.length, response.data);
        setScrapedSections(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch scraped sections:", error);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${id}/config`);
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch config");
    }
  };

  const fetchWebsiteContext = async () => {
    setLoadingWebsiteContext(true);
    try {
      const response = await axios.get(`${API}/campaigns/${id}/website-context`);
      setWebsiteContext(response.data);
    } catch (error) {
      console.error("Failed to fetch website context");
    } finally {
      setLoadingWebsiteContext(false);
    }
  };

  const [rescraping, setRescraping] = useState(false);
  
  const handleRescrape = async () => {
    setRescraping(true);
    try {
      toast.info("Re-scanning website...");
      const response = await axios.post(`${API}/campaigns/${id}/scrape`);
      setWebsiteContext(response.data.website_context);
      toast.success(`Website scanned! Found ${response.data.sections_count} sections`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scan website");
    } finally {
      setRescraping(false);
    }
  };

  const handleModeChange = async (newMode) => {
    setMode(newMode);
    try {
      await axios.put(`${API}/campaigns/${id}/mode?mode=${newMode}`);
      toast.success(`Switched to ${newMode === 'auto' ? 'Auto' : 'Manual'} mode`);
    } catch (error) {
      toast.error("Failed to update mode");
    }
  };

  const handleExtractContext = async () => {
    if (!chatInput.trim()) {
      toast.error("Please paste chat text first");
      return;
    }

    setExtracting(true);
    setExtractedContext(null);
    try {
      const response = await axios.post(`${API}/campaigns/${id}/extract-context`, {
        chat_text: chatInput
      });
      setExtractedContext(response.data);
      toast.success("Context extracted from chat");
    } catch (error) {
      toast.error("Failed to extract context from chat");
    } finally {
      setExtracting(false);
    }
  };

  const handleUseExtractedContext = () => {
    if (extractedContext) {
      const parts = [];
      if (extractedContext.who && extractedContext.who !== "Not specified in chat") {
        parts.push(`Who: ${extractedContext.who}`);
      }
      if (extractedContext.why && extractedContext.why !== "Not specified in chat") {
        parts.push(`Why: ${extractedContext.why}`);
      }
      if (extractedContext.goal && extractedContext.goal !== "Not specified in chat") {
        parts.push(`Goal: ${extractedContext.goal}`);
      }
      setCampaignContext(parts.join('\n\n'));
      setContextTab("write");
      toast.success("Context applied! You can edit it before proceeding.");
    }
  };

  const handleAutoProcess = async () => {
    if (!campaignContext.trim()) {
      toast.error("Please provide campaign context");
      return;
    }

    setAutoProcessing(true);
    try {
      const response = await axios.post(`${API}/campaigns/${id}/auto-process`, {
        campaign_id: id,
        campaign_context: campaignContext
      });
      
      setStrategySummary(response.data.strategy_summary);
      setConfig(response.data.config);
      
      // Set the magic link if returned
      if (response.data.magic_link) {
        setMagicLink(response.data.magic_link);
      }
      
      // Fetch updated data
      fetchGoals();
      fetchMessaging();
      fetchSectionStrategy();
      
      setStep("activation");
      
      // Show detailed success message
      const goalsCount = response.data.goals_selected || 0;
      const sectionsCount = response.data.sections_applied || 0;
      const changesCount = response.data.messaging_changes_selected || 0;
      const variant = response.data.variant_selected || 'impact';
      
      toast.success(
        `Campaign activated! AI selected ${goalsCount} goal(s), applied ${variant.toUpperCase()} mode to ${sectionsCount} section(s) with ${changesCount} changes.`
      );
    } catch (error) {
      console.error("Auto-processing failed:", error);
      toast.error("Auto-processing failed. Please try again or switch to Manual mode.");
    } finally {
      setAutoProcessing(false);
    }
  };

  const handleSubmitContext = async () => {
    if (!campaignContext.trim()) {
      toast.error("Please provide campaign context");
      return;
    }

    // If auto mode, run auto-process instead
    if (mode === "auto") {
      return handleAutoProcess();
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/campaigns/${id}/analyze`, {
        campaign_id: id,
        campaign_context: campaignContext
      });
      // Handle new outcome + strategy goals format
      setGoals(response.data.goals || []);
      setOutcomeGoal(response.data.outcome_goal || null);
      setVisitorAnalysis(response.data.visitor_analysis || null);
      setRelationshipExplainer(response.data.relationship_explainer || []);
      setExcludedGoals(response.data.excluded_goals || []);
      setStrategySummary(response.data.strategy_summary || "");
      setStep("goals");
      toast.success("Analysis complete! Review the outcome and strategy goals.");
    } catch (error) {
      toast.error("Analysis failed. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleToggleGoal = async (goal) => {
    try {
      await axios.put(`${API}/campaigns/${id}/goals/${goal.id}`, {
        goal_text: goal.goal_text,
        is_approved: !goal.is_approved
      });
      setGoals(goals.map(g => 
        g.id === goal.id ? { ...g, is_approved: !g.is_approved } : g
      ));
    } catch (error) {
      toast.error("Failed to update goal");
    }
  };

  const handleEditGoal = async (goalId, newText) => {
    try {
      await axios.put(`${API}/campaigns/${id}/goals/${goalId}`, {
        goal_text: newText,
        is_approved: true
      });
      setGoals(goals.map(g => 
        g.id === goalId ? { ...g, goal_text: newText } : g
      ));
      toast.success("Goal updated");
    } catch (error) {
      toast.error("Failed to update goal");
    }
  };

  const handleConfirmGoals = async () => {
    const approvedGoals = goals.filter(g => g.is_approved);
    if (approvedGoals.length === 0) {
      toast.error("Please approve at least one strategy goal");
      return;
    }

    setGeneratingStrategy(true);
    try {
      // Confirm goals first
      await axios.post(`${API}/campaigns/${id}/goals/confirm`).catch(() => {});
      
      // Generate section strategy (new approach)
      const response = await axios.post(`${API}/campaigns/${id}/section-strategy`);
      
      if (response.data.error) {
        toast.error(response.data.error);
        return;
      }
      
      setSectionStrategy(response.data);
      setStep("messaging");
      toast.success("Section strategy generated! Review and apply changes.");
    } catch (error) {
      toast.error("Failed to generate section strategy");
      // Fallback to old messaging system
      try {
        const response = await axios.post(`${API}/campaigns/${id}/generate-messaging`, {
          mode: messagingMode
        });
        setMessagingChanges(response.data.changes);
        setStep("messaging");
        toast.success(`${messagingMode === 'subtle' ? 'Subtle' : 'Impact'} messaging changes generated!`);
      } catch (fallbackError) {
        toast.error("Failed to generate messaging changes");
      }
    } finally {
      setGeneratingStrategy(false);
    }
  };

  // Section Strategy handlers
  const handleApplyVariant = async (sectionId, variantType) => {
    try {
      const response = await axios.post(`${API}/campaigns/${id}/apply-section-variant`, {
        section_id: sectionId,
        variant_type: variantType
      });
      
      // Refresh section strategy
      const strategyResponse = await axios.get(`${API}/campaigns/${id}/section-strategy`);
      if (!strategyResponse.data.error) {
        setSectionStrategy(strategyResponse.data);
      }
      
      toast.success(`Applied ${variantType} variant`);
    } catch (error) {
      toast.error("Failed to apply variant");
    }
  };
  
  // Handler for updating changes (edits to proposed text or adding new changes)
  const handleUpdateChanges = async (sectionId, mode, newChanges) => {
    try {
      // Update local state immediately for responsiveness
      setSectionStrategy(prev => {
        if (!prev) return prev;
        const updated = { ...prev };
        const sections = [...(updated.recommended_sections || [])];
        const sectionIndex = sections.findIndex(s => s.section_id === sectionId);
        
        if (sectionIndex !== -1) {
          const section = { ...sections[sectionIndex] };
          const variantKey = mode === 'subtle' ? 'subtle_variant' : 'impact_variant';
          if (section[variantKey]) {
            section[variantKey] = {
              ...section[variantKey],
              changes: newChanges
            };
          }
          sections[sectionIndex] = section;
          updated.recommended_sections = sections;
        }
        
        return updated;
      });
      
      // Persist to backend
      await axios.post(`${API}/campaigns/${id}/update-section-changes`, {
        section_id: sectionId,
        mode: mode,
        changes: newChanges
      });
      
    } catch (error) {
      console.error("Failed to update changes:", error);
      // Don't show error toast for now - local state update is enough
    }
  };

  const handleRevertSection = async (sectionId) => {
    try {
      // Find and delete the messaging changes for this section
      const changes = messagingChanges.filter(c => c.section_id === sectionId);
      for (const change of changes) {
        await axios.post(`${API}/campaigns/${id}/messaging/${change.id}/revert`);
      }
      
      // Refresh messaging changes and section strategy
      fetchMessaging();
      const strategyResponse = await axios.get(`${API}/campaigns/${id}/section-strategy`);
      if (!strategyResponse.data.error) {
        setSectionStrategy(strategyResponse.data);
      }
      
      toast.success("Section reverted");
    } catch (error) {
      toast.error("Failed to revert section");
    }
  };

  const handleConfirmMessaging = async () => {
    try {
      await axios.post(`${API}/campaigns/${id}/messaging/confirm`);
      setStep("activation");
      fetchConfig();
      toast.success("Messaging confirmed! Ready to activate.");
    } catch (error) {
      toast.error("Failed to confirm messaging");
    }
  };

  const handleToggleChange = async (change) => {
    try {
      await axios.put(`${API}/campaigns/${id}/messaging/${change.id}`, {
        is_approved: !change.is_approved
      });
      setMessagingChanges(messagingChanges.map(c => 
        c.id === change.id ? { ...c, is_approved: !c.is_approved } : c
      ));
    } catch (error) {
      toast.error("Failed to update change");
    }
  };

  const handleEditChange = (change) => {
    setEditingChangeId(change.id);
    setEditingChangeText(change.proposed_content);
  };

  const handleSaveChange = async (change) => {
    if (!editingChangeText.trim()) {
      toast.error("Proposed content cannot be empty");
      return;
    }
    
    setSavingChange(true);
    try {
      await axios.put(`${API}/campaigns/${id}/messaging/${change.id}`, {
        is_approved: change.is_approved,
        proposed_content: editingChangeText
      });
      setMessagingChanges(messagingChanges.map(c => 
        c.id === change.id ? { ...c, proposed_content: editingChangeText } : c
      ));
      setEditingChangeId(null);
      setEditingChangeText("");
      toast.success("Change updated successfully");
    } catch (error) {
      toast.error("Failed to save change");
    } finally {
      setSavingChange(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingChangeId(null);
    setEditingChangeText("");
  };

  // Old activate handler (for legacy messaging flow)
  const handleActivate = async () => {
    const approvedChanges = messagingChanges.filter(c => c.is_approved);
    if (approvedChanges.length === 0) {
      toast.error("Please approve at least one change");
      return;
    }

    setActivating(true);
    try {
      await axios.post(`${API}/campaigns/${id}/messaging/confirm`);
      const response = await axios.post(`${API}/campaigns/${id}/activate`);
      setConfig(response.data);
      setStep("activation");
      toast.success("Campaign activated!");
    } catch (error) {
      toast.error("Failed to activate campaign");
    } finally {
      setActivating(false);
    }
  };
  
  // New activate handler (for section strategy flow)
  const handleActivateCampaign = async () => {
    setActivating(true);
    try {
      const response = await axios.post(`${API}/campaigns/${id}/activate`);
      setConfig(response.data);
      toast.success("Campaign activated!");
    } catch (error) {
      toast.error("Failed to activate campaign");
    } finally {
      setActivating(false);
    }
  };

  const handleCopySnippet = async () => {
    const textToCopy = config?.js_snippet;
    if (!textToCopy) {
      toast.error("No snippet available to copy");
      return;
    }
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopied(true);
      toast.success("Snippet copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Final fallback
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toast.success("Snippet copied!");
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        toast.error("Copy failed. Please select and copy the code manually.");
      }
      textArea.remove();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                data-testid="back-to-dashboard-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Dashboard</span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-medium text-foreground truncate max-w-[200px]">{campaign?.name}</span>
              {campaign?.source === 'api' && (
                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded">
                  Created via API
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="border-b border-border bg-surface">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-center gap-2 md:gap-8">
            {["context", "goals", "messaging", "activation"].map((s, i) => {
              const stepLabels = { context: "Context", goals: "Goals", messaging: "Messaging", activation: "Activation" };
              const currentIndex = ["context", "goals", "messaging", "activation"].indexOf(step);
              const isCompleted = currentIndex > i;
              const isCurrent = step === s;
              const isClickable = isCompleted || isCurrent;
              
              return (
                <div key={s} className="flex items-center gap-2">
                  <button
                    onClick={() => isClickable && setStep(s)}
                    disabled={!isClickable}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      isCurrent ? "bg-primary text-white" : 
                      isCompleted ? "bg-success text-white hover:bg-success/80 cursor-pointer" : 
                      "bg-muted text-muted-foreground cursor-not-allowed"
                    }`}
                    data-testid={`step-${s}`}
                  >
                    {isCompleted ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      i + 1
                    )}
                  </button>
                  <button
                    onClick={() => isClickable && setStep(s)}
                    disabled={!isClickable}
                    className={`text-sm hidden md:block ${
                      isCurrent ? "text-foreground font-medium" : 
                      isCompleted ? "text-foreground hover:text-primary cursor-pointer" :
                      "text-muted-foreground cursor-not-allowed"
                    }`}
                  >
                    {stepLabels[s]}
                  </button>
                  {i < 3 && <div className="w-8 md:w-12 h-0.5 bg-border mx-2" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Context Step */}
        {step === "context" && (
          <div className="animate-fade-in" data-testid="context-step">
            <div className="text-center mb-12">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground font-['Outfit'] mb-3">
                Describe your campaign
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Tell us about your target visitor and what you want to achieve. Our AI will handle the rest.
              </p>
            </div>

            {/* Website Context Card - Extracted from scraped site */}
            {websiteContext && !loadingWebsiteContext && (
              <div className="mb-6 p-5 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700" data-testid="website-context-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                      <Globe className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground text-sm">Website Analysis</h4>
                      <p className="text-xs text-muted-foreground">Extracted from your site</p>
                    </div>
                  </div>
                  <button
                    onClick={handleRescrape}
                    disabled={rescraping}
                    className="text-xs px-3 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="rescrape-button"
                  >
                    {rescraping ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3" />
                        Re-scan
                      </>
                    )}
                  </button>
                </div>
                
                {/* Show empty state if no content was scraped */}
                {websiteContext.website_description === 'No content scraped yet' ? (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                      <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">Website scan incomplete</p>
                    <p className="text-xs text-muted-foreground mb-4">Click &quot;Re-scan&quot; to analyze the website</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">About</p>
                      <p className="text-sm text-foreground">{websiteContext.website_description || "Analyzing..."}</p>
                    </div>
                    
                    {/* CTAs - Button text */}
                    <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">CTAs (Button Text)</p>
                      <div className="flex flex-wrap gap-2">
                        {(websiteContext.ctas || []).map((item, i) => (
                          <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {item.text || item}
                          </span>
                        ))}
                        {(!websiteContext.ctas || websiteContext.ctas.length === 0) && (
                          <span className="text-xs text-muted-foreground">No CTAs detected</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Business Goals - Actual objectives */}
                    <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Business Goals</p>
                      <div className="space-y-2">
                        {(websiteContext.primary_goals || websiteContext.business_goals || []).map((g, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              g.priority === 'HIGH' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                            }`}>
                              {g.priority || 'GOAL'}
                            </span>
                            <span className="text-sm text-foreground">{g.goal}</span>
                          </div>
                        ))}
                        {(!(websiteContext.primary_goals || websiteContext.business_goals) || (websiteContext.primary_goals || websiteContext.business_goals).length === 0) && (
                          <span className="text-xs text-muted-foreground">No business goals detected</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Products/Services</p>
                      <div className="flex flex-wrap gap-2">
                        {(websiteContext.products_services || []).map((p, i) => (
                          <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            {p.name}
                          </span>
                        ))}
                        {(!websiteContext.products_services || websiteContext.products_services.length === 0) && (
                          <span className="text-xs text-muted-foreground">No products detected</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="mt-3 text-xs text-muted-foreground italic">
                  Business goals will be used as fallback if you don&apos;t specify a goal in your campaign context below.
                </p>
              </div>
            )}
            
            {loadingWebsiteContext && (
              <div className="mb-6 p-5 bg-muted/30 rounded-2xl border border-border flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Analyzing website...</span>
              </div>
            )}

            <div className="space-y-6 bg-surface rounded-2xl p-8 border border-border">
              {/* Context Input Tabs */}
              <div className="flex gap-2 p-1 bg-muted/50 rounded-xl">
                <button
                  onClick={() => setContextTab("write")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    contextTab === "write" 
                      ? "bg-background shadow-sm text-foreground" 
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="context-tab-write"
                >
                  <Edit2 className="w-4 h-4" />
                  Write Context
                </button>
                <button
                  onClick={() => setContextTab("chat")}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    contextTab === "chat" 
                      ? "bg-background shadow-sm text-foreground" 
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="context-tab-chat"
                >
                  <MessageSquare className="w-4 h-4" />
                  From Chat
                </button>
              </div>

              {/* Write Mode */}
              {contextTab === "write" && (
                <>
                  {/* Suggestion Pointers */}
                  <div className="bg-muted/30 rounded-xl p-4 border border-border/50">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Include details about:</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs text-primary font-medium">1</span>
                        </div>
                        <span className="text-muted-foreground"><strong className="text-foreground">Who</strong> — visitor persona, background, pain points</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs text-primary font-medium">2</span>
                        </div>
                        <span className="text-muted-foreground"><strong className="text-foreground">Why</strong> — their intent, what they're looking for</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs text-primary font-medium">3</span>
                        </div>
                        <span className="text-muted-foreground"><strong className="text-foreground">Goal</strong> — desired action (demo, signup, purchase)</span>
                      </div>
                    </div>
                  </div>

                  {/* Single Context Textarea */}
                  <div className="space-y-2">
                    <Label htmlFor="campaign-context" className="text-foreground font-medium">
                      Campaign Context
                    </Label>
                    <Textarea
                      id="campaign-context"
                      placeholder="Example: This campaign targets enterprise security teams evaluating compliance solutions. They're typically risk-averse, need stakeholder buy-in, and are comparing multiple vendors. Our goal is to get them to book a demo by highlighting our SOC2 certification and enterprise support."
                      value={campaignContext}
                      onChange={(e) => setCampaignContext(e.target.value)}
                      className="min-h-[180px] resize-none"
                      data-testid="campaign-context-input"
                    />
                  </div>
                </>
              )}

              {/* Chat Mode */}
              {contextTab === "chat" && (
                <div className="space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Paste a chat conversation</strong> with your visitor/customer. Our AI will extract the context without making up any information.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="chat-input" className="text-foreground font-medium">
                      Chat Transcript
                    </Label>
                    <Textarea
                      id="chat-input"
                      placeholder="Paste your chat conversation here...

Example:
User: Hi, I'm looking for a project management tool for my team
Agent: Great! How big is your team?
User: About 50 people, we're a marketing agency
Agent: What's your main challenge right now?
User: We're struggling with tracking deadlines across multiple client projects..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      className="min-h-[200px] resize-none font-mono text-sm"
                      data-testid="chat-input"
                    />
                  </div>

                  <Button
                    onClick={handleExtractContext}
                    disabled={extracting || !chatInput.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-5"
                    data-testid="extract-context-btn"
                  >
                    {extracting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Extracting context from chat...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Extract Context
                      </>
                    )}
                  </Button>

                  {/* Extracted Context Output */}
                  {extractedContext && (
                    <div className="mt-6 p-5 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-xl border border-green-200 dark:border-green-800 animate-fade-in">
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <h4 className="font-semibold text-green-800 dark:text-green-200">Extracted Context</h4>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Who — Visitor Persona</p>
                          <p className="text-sm text-foreground">{extractedContext.who || "Not specified in chat"}</p>
                        </div>
                        
                        <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Why — Intent</p>
                          <p className="text-sm text-foreground">{extractedContext.why || "Not specified in chat"}</p>
                        </div>
                        
                        <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Goal — Desired Action</p>
                          <p className="text-sm text-foreground">{extractedContext.goal || "Not specified in chat"}</p>
                        </div>

                        {extractedContext.missing_info && extractedContext.missing_info.length > 0 && (
                          <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                            <p className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide mb-1">
                              <AlertCircle className="w-3 h-3 inline mr-1" />
                              Information not found in chat
                            </p>
                            <p className="text-sm text-amber-800 dark:text-amber-200">{extractedContext.missing_info.join(", ")}</p>
                          </div>
                        )}
                      </div>

                      <Button
                        onClick={handleUseExtractedContext}
                        className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white rounded-full"
                        data-testid="use-extracted-context-btn"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Use This Context
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Mode Toggle - Only show in Write mode */}
              {contextTab === "write" && (
                <>
                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center justify-between py-3 px-4 bg-muted/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${mode === 'auto' ? 'bg-primary' : 'bg-muted'}`}>
                          {mode === 'auto' ? (
                            <Zap className="w-5 h-5 text-white" />
                          ) : (
                            <Settings2 className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <Label htmlFor="workspace-mode-toggle" className="font-medium cursor-pointer">
                            {mode === 'auto' ? 'Auto Mode' : 'Manual Mode'}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {mode === 'auto' 
                              ? "AI will select best concerns & messaging automatically" 
                              : "You'll review and approve each step manually"}
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="workspace-mode-toggle"
                        checked={mode === 'auto'}
                        onCheckedChange={(checked) => handleModeChange(checked ? 'auto' : 'manual')}
                        data-testid="workspace-mode-toggle"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleSubmitContext}
                    disabled={analyzing || autoProcessing}
                    className={`w-full rounded-full py-6 ${mode === 'auto' ? 'bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90' : 'bg-primary hover:bg-primary/90'} text-white`}
                    data-testid="analyze-btn"
                  >
                    {analyzing || autoProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {mode === 'auto' ? 'AI is processing your campaign...' : 'Identifying campaign goals...'}
                      </>
                    ) : mode === 'auto' ? (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Auto-Generate & Activate Campaign
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        <Target className="w-4 h-4 mr-2" />
                        Identify Campaign Goals
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Goals Step */}
        {step === "goals" && (
          <div className="animate-fade-in" data-testid="goals-step">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground font-['Outfit'] mb-3">
                Review Campaign Goals
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Select the goals that best align with your campaign objectives.
              </p>
            </div>

            {/* Inline Goals Display */}
            <div className="space-y-6" data-testid="goals-display">
              {/* Outcome Goal - Always shown first */}
              {outcomeGoal && (
                <OutcomeGoalCard outcomeGoal={outcomeGoal} />
              )}

              {/* Strategy Summary */}
              {strategySummary && (
                <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-4 border border-primary/20">
                  <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" /> Strategy Summary
                  </h3>
                  <p className="text-sm text-muted-foreground">{strategySummary}</p>
                </div>
              )}

              {/* Relationship Explainer - How strategies drive outcome */}
              {relationshipExplainer && relationshipExplainer.length > 0 && (
                <RelationshipExplainerCard items={relationshipExplainer} />
              )}

              {visitorAnalysis && visitorAnalysis.primary_intent && (
                <div className="bg-muted/30 rounded-xl p-4 border border-border">
                  <h3 className="font-semibold text-foreground mb-3 text-sm">Visitor Analysis</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <span className="ml-1 font-medium capitalize">{String(visitorAnalysis.visitor_type || '').replace('_', ' ')}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Stage:</span>
                      <span className="ml-1 font-medium capitalize">{String(visitorAnalysis.visitor_stage || '').replace('_', ' ')}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Channel:</span>
                      <span className="ml-1 font-medium capitalize">{visitorAnalysis.source_channel}</span>
                    </div>
                    <div className="col-span-2 md:col-span-4">
                      <span className="text-muted-foreground">Intent:</span>
                      <span className="ml-1 font-medium">{visitorAnalysis.primary_intent}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">
                  Strategy Goals ({goals.filter(g => g.is_approved).length}/{goals.length} selected)
                </h3>
              </div>

              <div className="space-y-4">
                {goals.map((goal) => (
                  <GoalCard 
                    key={goal.id}
                    goal={goal}
                    onToggle={handleToggleGoal}
                  />
                ))}
              </div>

              {goals.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No strategy goals identified yet.</p>
                </div>
              )}

              {/* Excluded Goals */}
              {excludedGoals && excludedGoals.length > 0 && (
                <ExcludedGoalsCard items={excludedGoals} />
              )}
            </div>

            <div className="mt-8 space-y-4">
              {/* Mode Selector */}
              <div className="bg-muted/30 rounded-xl p-4">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Change Intensity
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setMessagingMode('subtle')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      messagingMode === 'subtle'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="mode-subtle-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-foreground">Subtle</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Minor refinements (10-20% changes). Polish existing copy with strategic tweaks.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMessagingMode('impact')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      messagingMode === 'impact'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid="mode-impact-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-foreground">Impact</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Bold transformations. Rewrite copy for maximum conversion.
                    </p>
                  </button>
                </div>
              </div>

              <Button
                onClick={handleConfirmGoals}
                disabled={generatingStrategy}
                className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-6"
                data-testid="confirm-goals-btn"
              >
                {generatingStrategy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating section strategy...
                  </>
                ) : (
                  <>
                    Generate Section Strategy
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Messaging Step - New Section-Based UI */}
        {step === "messaging" && (
          <div className="animate-fade-in" data-testid="messaging-step">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground font-['Outfit'] mb-3">
                Review Section Changes
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Apply controlled changes section-by-section. Choose Subtle for safe refinements or Impact for bolder transformations.
              </p>
            </div>

            {sectionStrategy ? (
              <MessagingStep
                sectionStrategy={sectionStrategy}
                scrapedSections={scrapedSections}
                onApplyVariant={handleApplyVariant}
                onRevertSection={handleRevertSection}
                onUpdateChanges={handleUpdateChanges}
                onConfirmMessaging={handleConfirmMessaging}
                isLoading={activating}
              />
            ) : (
              /* Fallback to old messaging UI if no section strategy */
              <div className="space-y-6 mb-8">
                {messagingChanges.map((change) => (
                  <OldMessagingChangeCard
                    key={change.id}
                    change={change}
                    onToggle={handleToggleChange}
                    onEdit={handleEditChange}
                    onSave={handleSaveChange}
                    onCancel={handleCancelEdit}
                    editingId={editingChangeId}
                    editingText={editingChangeText}
                    setEditingText={setEditingChangeText}
                    saving={savingChange}
                  />
                ))}
                
                <Button
                  onClick={handleActivate}
                  disabled={activating}
                  className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-6"
                  data-testid="activate-campaign-btn"
                >
                  {activating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Activating campaign...
                    </>
                  ) : (
                    <>
                      Activate Campaign
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Activation Step - Pre-activation (no config yet) */}
        {step === "activation" && !config && (
          <div className="animate-fade-in" data-testid="pre-activation-step">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground font-['Outfit'] mb-3">
                Ready to Activate
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Your messaging changes are ready. Activate your campaign to generate the Magic Link and JavaScript snippet.
              </p>
            </div>
            
            <Button
              onClick={handleActivateCampaign}
              disabled={activating}
              className="w-full max-w-md mx-auto bg-primary text-white hover:bg-primary/90 rounded-full py-6 flex items-center justify-center"
              data-testid="activate-campaign-btn"
            >
              {activating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Activating campaign...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Activate Campaign
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Activation Step - Post-activation (config exists) */}
        {step === "activation" && config && (
          <div className="animate-fade-in" data-testid="activation-step">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-success/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                {mode === 'auto' ? (
                  <Zap className="w-8 h-8 text-success" strokeWidth={1.5} />
                ) : (
                  <CheckCircle2 className="w-8 h-8 text-success" strokeWidth={1.5} />
                )}
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground font-['Outfit'] mb-3">
                Campaign Activated!
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                {mode === 'auto' ? 'AI has selected the best concerns and messaging for your campaign' : 'Choose how you want to deploy your personalized messaging'}
              </p>
            </div>

            {/* AI Strategy Summary (Auto mode only) */}
            {mode === 'auto' && strategySummary && (
              <div className="mb-8 p-4 bg-gradient-to-r from-primary/5 to-blue-500/5 rounded-xl border border-primary/20">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">AI Strategy Summary</h4>
                    <p className="text-sm text-muted-foreground">{strategySummary}</p>
                  </div>
                </div>
              </div>
            )}

            <Tabs defaultValue="no-embed" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="no-embed" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-full">
                  <Wand2 className="w-4 h-4 mr-2" />
                  No Embed (Magic Link)
                </TabsTrigger>
                <TabsTrigger value="embed-js" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-full">
                  <Code className="w-4 h-4 mr-2" />
                  Embed JS
                </TabsTrigger>
              </TabsList>

              {/* No Embed Tab - Magic Link */}
              <TabsContent value="no-embed" className="space-y-6">
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 border border-primary/20">
                  <div className="text-center">
                    <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Wand2 className="w-7 h-7 text-primary" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground font-['Outfit'] mb-2">
                      Magic Link
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      No code installation required. We'll create a special link that shows your website with campaign changes already applied.
                    </p>
                    
                    {!magicLink ? (
                      <Button
                        onClick={async () => {
                          setCreatingMagicLink(true);
                          try {
                            const response = await axios.post(`${API}/campaigns/${id}/magic-link`);
                            setMagicLink(response.data.magic_link);
                            toast.success("Magic Link created!");
                          } catch (error) {
                            toast.error("Failed to create Magic Link");
                          } finally {
                            setCreatingMagicLink(false);
                          }
                        }}
                        disabled={creatingMagicLink}
                        size="lg"
                        className="bg-primary text-white hover:bg-primary/90 rounded-full px-8"
                        data-testid="create-magic-link-btn"
                      >
                        {creatingMagicLink ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-5 h-5 mr-2" />
                            Create My Magic Link
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex gap-2 max-w-xl mx-auto">
                          <Input
                            value={magicLink}
                            readOnly
                            className="font-mono text-sm bg-white"
                            data-testid="magic-link-input"
                          />
                          <Button
                            variant="outline"
                            onClick={async () => {
                              try {
                                if (navigator.clipboard && window.isSecureContext) {
                                  await navigator.clipboard.writeText(magicLink);
                                } else {
                                  const textArea = document.createElement('textarea');
                                  textArea.value = magicLink;
                                  textArea.style.position = 'fixed';
                                  textArea.style.left = '-999999px';
                                  document.body.appendChild(textArea);
                                  textArea.focus();
                                  textArea.select();
                                  document.execCommand('copy');
                                  textArea.remove();
                                }
                                toast.success("Magic Link copied!");
                              } catch (e) {
                                toast.error("Please copy manually");
                              }
                            }}
                            data-testid="copy-magic-link-btn"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => window.open(magicLink, '_blank')}
                            data-testid="open-magic-link-btn"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Share this link with anyone — they'll see your website with campaign changes applied
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-surface rounded-xl p-6 border border-border">
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-success" />
                    Perfect for
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li>• Quick demos and presentations</li>
                    <li>• Testing campaigns before installing code</li>
                    <li>• Sharing with stakeholders for approval</li>
                    <li>• Websites you don't have code access to</li>
                  </ul>
                </div>
              </TabsContent>

              {/* Embed JS Tab */}
              <TabsContent value="embed-js" className="space-y-6">
                {/* Campaign URL */}
                <div className="bg-surface rounded-xl p-6 border border-border">
                  <h3 className="font-medium text-foreground mb-2">Campaign URL</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Share this URL or add <code className="bg-muted px-1.5 py-0.5 rounded text-xs">?pravik={id}</code> to any page
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={campaign?.url ? `${campaign.url}?pravik=${id}` : ''}
                      readOnly
                      className="font-mono text-sm"
                      data-testid="campaign-url"
                    />
                    <Button
                      variant="outline"
                      onClick={async () => {
                        const textToCopy = campaign?.url ? `${campaign.url}?pravik=${id}` : '';
                        if (!textToCopy) return;
                        try {
                          if (navigator.clipboard && window.isSecureContext) {
                            await navigator.clipboard.writeText(textToCopy);
                          } else {
                            const textArea = document.createElement('textarea');
                            textArea.value = textToCopy;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-999999px';
                            document.body.appendChild(textArea);
                            textArea.focus();
                            textArea.select();
                            document.execCommand('copy');
                            textArea.remove();
                          }
                          toast.success("URL copied!");
                        } catch (e) {
                          toast.error("Please copy manually");
                        }
                      }}
                      data-testid="copy-url-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* JS Snippet */}
                <div className="bg-surface rounded-xl border border-border overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <div className="flex items-center gap-2">
                      <Code className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium text-foreground">JavaScript Snippet</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopySnippet}
                      className="copy-btn"
                      data-testid="copy-snippet-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-1 text-success" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <ScrollArea className="h-[250px]">
                    <pre className="p-4 text-sm font-mono text-foreground overflow-x-auto">
                      <code>{config.js_snippet}</code>
                    </pre>
                  </ScrollArea>
                </div>

                {/* Instructions */}
                <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-foreground mb-2">How to install</h4>
                      <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                        <li>Add the snippet to your website's <code className="bg-muted px-1 py-0.5 rounded">&lt;head&gt;</code> or before <code className="bg-muted px-1 py-0.5 rounded">&lt;/body&gt;</code></li>
                        <li>The snippet works for all your campaigns automatically</li>
                        <li>When a visitor arrives with <code className="bg-muted px-1 py-0.5 rounded">?pravik={id}</code>, they'll see the personalized content</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-8 flex justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="rounded-full"
                data-testid="back-to-dashboard-final"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
